import { useMemo, useState } from "react"
import { Building2, Cylinder, Filter, MapPin, Plus, ArrowDownUp } from "lucide-react"
import { useTranslation } from "react-i18next"
import { T } from "../theme"
import {
  BottomSheet,
  MButton,
  MHeader,
  MStageBadge,
  SearchBar,
  SectionLabel,
  Touchable,
} from "../primitives"
import { MultiPicker, SearchablePicker } from "../SearchablePicker"
import type {
  AssetTypeConfig,
  AssetTypeKey,
  Container,
  Location as LocationRef,
  MobileAsset,
  Product,
  Stage,
  Tag,
} from "../types"

interface InventoryScreenProps {
  assets: MobileAsset[]
  stages: Stage[]
  assetTypes: AssetTypeConfig[]
  products: Product[]
  containers: Container[]
  locations: LocationRef[]
  tags: Tag[]
  onOpenAsset: (asset: MobileAsset) => void
  onAddAsset?: () => void
}

interface FilterState {
  /** Empty string means "all"; otherwise the id of the selected reference. */
  assetType: string
  stage: string
  product: string
  container: string
  location: string
  /** Multi-select; an asset matches if it carries any of the selected tags. */
  tagIds: string[]
}

const EMPTY_FILTERS: FilterState = {
  assetType: "",
  stage: "",
  product: "",
  container: "",
  location: "",
  tagIds: [],
}

export function InventoryScreen({
  assets,
  stages,
  assetTypes,
  products,
  containers,
  locations,
  tags,
  onOpenAsset,
  onAddAsset,
}: InventoryScreenProps) {
  const { t } = useTranslation()
  const [search, setSearch] = useState("")
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState<FilterState>(EMPTY_FILTERS)

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    const tagIdSet = new Set(filters.tagIds)
    return assets.filter((r) => {
      if (q) {
        const haystack = [
          r.code,
          r.product?.name,
          r.container?.name,
          r.customer?.name,
          r.location?.name,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
        if (!haystack.includes(q)) return false
      }
      if (filters.assetType && r.assetable_type !== filters.assetType) return false
      if (filters.stage && String(r.stage.id) !== filters.stage) return false
      if (filters.product && String(r.product?.id ?? "") !== filters.product) return false
      if (filters.container && String(r.container?.id ?? "") !== filters.container) return false
      if (filters.location && String(r.location?.id ?? "") !== filters.location) return false
      if (tagIdSet.size > 0) {
        const hasAny = (r.tags || []).some((tag) => tagIdSet.has(String(tag.id)))
        if (!hasAny) return false
      }
      return true
    })
  }, [assets, search, filters])

  // Count distinct active filter groups (tags counts as one group, regardless of size).
  const activeFiltersCount =
    (filters.assetType ? 1 : 0) +
    (filters.stage ? 1 : 0) +
    (filters.product ? 1 : 0) +
    (filters.container ? 1 : 0) +
    (filters.location ? 1 : 0) +
    (filters.tagIds.length > 0 ? 1 : 0)

  return (
    <div>
      <MHeader
        title={t("mobile.tabs.inventory")}
        subtitle={t("mobile.inventory.subtitle", { count: assets.length })}
        rightIcon={onAddAsset ? Plus : undefined}
        onRightPress={onAddAsset}
      />

      {/* Search + filter trigger */}
      <div
        style={{
          padding: "12px 16px 8px",
          background: T.surface,
          borderBottom: `1px solid ${T.borderSoft}`,
          position: "sticky",
          top: T.headerHeight,
          zIndex: 5,
        }}
      >
        <div style={{ display: "flex", gap: 8 }}>
          <div style={{ flex: 1 }}>
            <SearchBar
              value={search}
              onChange={setSearch}
              placeholder={t("mobile.inventory.searchPlaceholder")}
            />
          </div>
          <Touchable
            onClick={() => setFiltersOpen(true)}
            style={{
              width: 44, height: 44, borderRadius: T.radius,
              background: activeFiltersCount > 0 ? T.accent : T.surface,
              color: activeFiltersCount > 0 ? "#fff" : T.ink,
              border: `1px solid ${activeFiltersCount > 0 ? T.accent : T.border}`,
              display: "grid", placeItems: "center", position: "relative",
            }}
          >
            <Filter size={18} color={activeFiltersCount > 0 ? "#fff" : T.ink} />
            {activeFiltersCount > 0 && (
              <span
                style={{
                  position: "absolute", top: -4, right: -4, minWidth: 18, height: 18,
                  padding: "0 5px", borderRadius: 999, background: T.ink, color: "#fff",
                  fontFamily: T.fontMono, fontSize: 10, fontWeight: 700,
                  display: "grid", placeItems: "center",
                  border: `2px solid ${T.surface}`,
                }}
              >
                {activeFiltersCount}
              </span>
            )}
          </Touchable>
        </div>
      </div>

      {/* List */}
      <div style={{ padding: "12px 16px 100px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            padding: "0 4px",
          }}
        >
          <span
            style={{
              fontFamily: T.fontMono,
              fontSize: 11,
              color: T.inkSoft,
              letterSpacing: 1,
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            {t("mobile.inventory.results", { count: filtered.length })}
          </span>
          <Touchable
            style={{ display: "inline-flex", alignItems: "center", gap: 4, color: T.inkSoft }}
          >
            <ArrowDownUp size={13} color={T.inkSoft} />
            <span style={{ fontFamily: T.fontBody, fontSize: 12.5, color: T.inkSoft }}>
              {t("mobile.inventory.sortByRecent")}
            </span>
          </Touchable>
        </div>
        {filtered.length === 0 && (
          <div
            style={{
              padding: "40px 20px",
              textAlign: "center",
              color: T.inkSoft,
              fontSize: 14,
            }}
          >
            {t("mobile.inventory.noResults")}
          </div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {filtered.map((r) => (
            <AssetCard key={r.id} asset={r} onClick={() => onOpenAsset(r)} />
          ))}
        </div>
      </div>

      {/* Filter bottom sheet */}
      <BottomSheet
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        title={t("mobile.filters.title")}
      >
        <div style={{ padding: "8px 16px 24px" }}>
          <PickerFilter
            label={t("mobile.assetTypes.label")}
            allLabel={t("inventory.filters.all")}
            options={assetTypes.map((c) => ({
              id: c.type,
              name: t(`mobile.assetTypes.${c.key}`),
            }))}
            value={filters.assetType}
            onChange={(v) => setFilters((s) => ({ ...s, assetType: v as AssetTypeKey | "" }))}
            searchable={false}
          />
          <PickerFilter
            label={t("inventory.filters.stage")}
            allLabel={t("inventory.filters.all")}
            options={stages}
            value={filters.stage}
            onChange={(v) => setFilters((s) => ({ ...s, stage: v }))}
            searchable={false}
            renderLeading={(option) => {
              // The picker only knows about { id, name } — pull the original
              // Stage record back out by id so we can use its color.
              const stage = stages.find((s) => String(s.id) === String(option.id))
              if (!stage) return null
              return (
                <span
                  aria-hidden
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: 999,
                    background: stage.color,
                    border:
                      stage.color.toLowerCase() === "#ffffff"
                        ? `1px solid ${T.ink}`
                        : "none",
                    flexShrink: 0,
                  }}
                />
              )
            }}
          />
          <PickerFilter
            label={t("inventory.filters.product")}
            allLabel={t("inventory.filters.all")}
            options={products}
            value={filters.product}
            onChange={(v) => setFilters((s) => ({ ...s, product: v }))}
          />
          <PickerFilter
            label={t("inventory.filters.container")}
            allLabel={t("inventory.filters.all")}
            options={containers}
            value={filters.container}
            onChange={(v) => setFilters((s) => ({ ...s, container: v }))}
          />
          <PickerFilter
            label={t("inventory.filters.location")}
            allLabel={t("inventory.filters.all")}
            options={locations}
            value={filters.location}
            onChange={(v) => setFilters((s) => ({ ...s, location: v }))}
          />
          {tags.length > 0 && (
            <MultiPickerFilter
              label={t("inventory.filters.tag")}
              allLabel={t("inventory.filters.all")}
              options={tags}
              value={filters.tagIds}
              onChange={(ids) => setFilters((s) => ({ ...s, tagIds: ids }))}
              renderLeading={(option) => {
                const tag = tags.find((tg) => String(tg.id) === String(option.id))
                if (!tag) return null
                return (
                  <span
                    aria-hidden
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: 999,
                      background: tag.color,
                      border:
                        tag.color.toLowerCase() === "#ffffff"
                          ? `1px solid ${T.ink}`
                          : "none",
                      flexShrink: 0,
                    }}
                  />
                )
              }}
            />
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
            <MButton
              variant="ghost"
              fullWidth
              onClick={() => setFilters(EMPTY_FILTERS)}
            >
              {t("mobile.filters.clear")}
            </MButton>
            <MButton variant="accent" fullWidth onClick={() => setFiltersOpen(false)}>
              {t("mobile.filters.apply")}
            </MButton>
          </div>
        </div>
      </BottomSheet>
    </div>
  )
}

interface AssetCardProps {
  asset: MobileAsset
  onClick?: () => void
}

function AssetCard({ asset, onClick }: AssetCardProps) {
  const subtitle = [asset.product?.name, asset.container?.name].filter(Boolean).join(" · ")
  const place = asset.customer?.name || asset.location?.name || "—"
  const isCustomer = Boolean(asset.customer)
  return (
    <Touchable
      onClick={onClick}
      style={{
        background: T.surface,
        borderRadius: T.radius,
        border: `1px solid ${T.border}`,
        padding: 14,
        display: "flex",
        gap: 14,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute", left: 0, top: 0, bottom: 0,
          width: 3, background: asset.stage.color,
        }}
      />
      <div
        style={{
          width: 48, height: 48, flexShrink: 0,
          background: T.surfaceAlt, color: T.inkSoft,
          border: `1px solid ${T.border}`,
          display: "grid", placeItems: "center",
        }}
      >
        <Cylinder size={22} color={T.inkSoft} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginBottom: 4,
          }}
        >
          <span
            style={{
              fontFamily: T.fontMono,
              fontSize: 14,
              fontWeight: 700,
              color: T.ink,
              letterSpacing: 0.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {asset.code}
          </span>
          <MStageBadge stage={asset.stage} size="sm" />
        </div>
        {subtitle && (
          <div
            style={{
              display: "flex",
              gap: 8,
              fontSize: 13,
              color: T.ink,
              marginBottom: 6,
            }}
          >
            <span style={{ fontWeight: 500 }}>{subtitle}</span>
          </div>
        )}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontSize: 12,
            color: T.inkSoft,
          }}
        >
          {isCustomer ? (
            <Building2 size={12} color={T.inkSoft} />
          ) : (
            <MapPin size={12} color={T.inkSoft} />
          )}
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {place}
          </span>
        </div>
      </div>
    </Touchable>
  )
}

interface PickerFilterProps {
  label: string
  /** Placeholder shown when nothing's selected (i.e. "all"). */
  allLabel: string
  options: { id: string | number; name: string }[]
  value: string
  onChange: (v: string) => void
  /** Pass `false` for short, predictable lists (stages) where search is overkill. */
  searchable?: boolean
  /** Optional element rendered before each row's label (e.g. a stage color dot). */
  renderLeading?: (option: { id: string | number; name: string }) => React.ReactNode
}

function PickerFilter({
  label,
  allLabel,
  options,
  value,
  onChange,
  searchable,
  renderLeading,
}: PickerFilterProps) {
  return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel>{label}</SectionLabel>
      <SearchablePicker
        value={value}
        onChange={onChange}
        options={options}
        title={label}
        placeholder={allLabel}
        allowClear={Boolean(value)}
        searchable={searchable}
        renderLeading={renderLeading}
      />
    </div>
  )
}

interface MultiPickerFilterProps {
  label: string
  allLabel: string
  options: { id: string | number; name: string }[]
  value: string[]
  onChange: (ids: string[]) => void
  /** Tag lists are usually short — `false` hides the search input. */
  searchable?: boolean
  renderLeading?: (option: { id: string | number; name: string }) => React.ReactNode
}

function MultiPickerFilter({
  label,
  allLabel,
  options,
  value,
  onChange,
  searchable = false,
  renderLeading,
}: MultiPickerFilterProps) {
  return (
    <div style={{ marginBottom: 18 }}>
      <SectionLabel>{label}</SectionLabel>
      <MultiPicker
        value={value}
        onChange={onChange}
        options={options}
        title={label}
        placeholder={allLabel}
        searchable={searchable}
        renderLeading={renderLeading}
      />
    </div>
  )
}
